import {
  AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { ChartCard } from './ChartCard'
import { formatPercent } from './format'
import type { CpuLatest, SeriesPoint } from '../../types/monitoring'

export function CpuPanel({ latest, series, loading, error }: {
  latest: CpuLatest | undefined; series: SeriesPoint[]; loading: boolean; error?: string
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <ChartCard title="CPU Usage" subtitle="System-wide utilization over time" loading={loading} error={error} isEmpty={!series.length}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={series} margin={{ left: -20, right: 8 }}>
              <defs>
                <linearGradient id="cpuGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#a3a3a3' }} axisLine={false} tickLine={false}
                tickFormatter={(t: string) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#a3a3a3' }} axisLine={false} tickLine={false} />
              <Tooltip
                formatter={(v: number) => [`${v.toFixed(1)}%`, 'CPU']}
                labelFormatter={(l) => new Date(l).toLocaleString()}
              />
              <Area type="monotone" dataKey="percent" stroke="#2563eb" strokeWidth={2} fill="url(#cpuGradient)" isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6">
        <h2 className="font-bold text-secondary mb-4">CPU Details</h2>
        {loading ? (
          <div className="h-56 bg-neutral-100 rounded-lg animate-pulse" />
        ) : (
          <div className="space-y-3 text-sm">
            <Row label="Load average (1m / 5m / 15m)" value={
              latest?.load_avg_1m != null
                ? `${latest.load_avg_1m.toFixed(2)} / ${latest.load_avg_5m?.toFixed(2)} / ${latest.load_avg_15m?.toFixed(2)}`
                : '—'
            } />
            <Row label="Logical / physical cores" value={`${latest?.logical_count ?? '—'} / ${latest?.physical_count ?? '—'}`} />
            <Row label="Frequency" value={latest?.freq_current_mhz ? `${(latest.freq_current_mhz / 1000).toFixed(2)} GHz` : '—'} />
            <Row label="Temperature" value={latest?.temperature_c != null ? `${latest.temperature_c.toFixed(0)}°C` : 'Unavailable'} />
          </div>
        )}
      </div>
    </div>
  )
}

export function CpuCorePanel({ latest, loading, error }: {
  latest: CpuLatest | undefined; loading: boolean; error?: string
}) {
  const perCore = latest?.per_core || []
  return (
    <ChartCard title="Per-Core Utilization" loading={loading} error={error} isEmpty={!perCore.length} height="h-40">
      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={perCore.map((v, i) => ({ core: `C${i}`, percent: v }))} margin={{ left: -20, right: 8 }}>
          <CartesianGrid vertical={false} stroke="#f0f0f0" />
          <XAxis dataKey="core" tick={{ fontSize: 10, fill: '#a3a3a3' }} axisLine={false} tickLine={false} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#a3a3a3' }} axisLine={false} tickLine={false} />
          <Tooltip formatter={(v: number) => [`${v.toFixed(1)}%`, 'Usage']} />
          <Bar dataKey="percent" radius={[4, 4, 0, 0]} barSize={18}>
            {perCore.map((v, i) => (
              <Cell key={i} fill={v > 90 ? '#dc2626' : v > 70 ? '#f59e0b' : '#2563eb'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-neutral-50 last:border-0">
      <span className="text-neutral-500">{label}</span>
      <span className="font-semibold text-neutral-800 tabular-nums">{value}</span>
    </div>
  )
}


// import {
//   AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
// } from 'recharts'
// import { ChartCard } from './ChartCard'
// import { formatPercent } from './format'
// import type { CpuLatest, SeriesPoint } from '../../types/monitoring'

// export function CpuPanel({ latest, series, loading, error }: {
//   latest: CpuLatest | undefined; series: SeriesPoint[]; loading: boolean; error?: string
// }) {
//   const perCore = latest?.per_core || []

//   return (
//     <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
//       <div className="lg:col-span-2">
//         <ChartCard title="CPU Usage" subtitle="System-wide utilization over time" loading={loading} error={error} isEmpty={!series.length}>
//           <ResponsiveContainer width="100%" height={220}>
//             <AreaChart data={series} margin={{ left: -20, right: 8 }}>
//               <defs>
//                 <linearGradient id="cpuGradient" x1="0" y1="0" x2="0" y2="1">
//                   <stop offset="0%" stopColor="#2563eb" stopOpacity={0.35} />
//                   <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
//                 </linearGradient>
//               </defs>
//               <CartesianGrid vertical={false} stroke="#f0f0f0" />
//               <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#a3a3a3' }} axisLine={false} tickLine={false}
//                 tickFormatter={(t: string) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} />
//               <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#a3a3a3' }} axisLine={false} tickLine={false} />
//               <Tooltip
//                 formatter={(v: number) => [`${v.toFixed(1)}%`, 'CPU']}
//                 labelFormatter={(l) => new Date(l).toLocaleString()}
//               />
//               <Area type="monotone" dataKey="percent" stroke="#2563eb" strokeWidth={2} fill="url(#cpuGradient)" isAnimationActive={false} />
//             </AreaChart>
//           </ResponsiveContainer>
//         </ChartCard>
//       </div>

//       <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6">
//         <h2 className="font-bold text-secondary mb-4">CPU Details</h2>
//         {loading ? (
//           <div className="h-56 bg-neutral-100 rounded-lg animate-pulse" />
//         ) : (
//           <div className="space-y-3 text-sm">
//             <Row label="Load average (1m / 5m / 15m)" value={
//               latest?.load_avg_1m != null
//                 ? `${latest.load_avg_1m.toFixed(2)} / ${latest.load_avg_5m?.toFixed(2)} / ${latest.load_avg_15m?.toFixed(2)}`
//                 : '—'
//             } />
//             <Row label="Logical / physical cores" value={`${latest?.logical_count ?? '—'} / ${latest?.physical_count ?? '—'}`} />
//             <Row label="Frequency" value={latest?.freq_current_mhz ? `${(latest.freq_current_mhz / 1000).toFixed(2)} GHz` : '—'} />
//             <Row label="Temperature" value={latest?.temperature_c != null ? `${latest.temperature_c.toFixed(0)}°C` : 'Unavailable'} />
//           </div>
//         )}
//       </div>
//     </div>
//   )
// }

// export function CpuCorePanel({ latest, loading, error }: {
//   latest: CpuLatest | undefined; loading: boolean; error?: string
// }) {
//   const perCore = latest?.per_core || []
//   return (
//     <ChartCard title="Per-Core Utilization" loading={loading} error={error} isEmpty={!perCore.length} height="h-40">
//       <ResponsiveContainer width="100%" height={160}>
//         <BarChart data={perCore.map((v, i) => ({ core: `C${i}`, percent: v }))} margin={{ left: -20, right: 8 }}>
//           <CartesianGrid vertical={false} stroke="#f0f0f0" />
//           <XAxis dataKey="core" tick={{ fontSize: 10, fill: '#a3a3a3' }} axisLine={false} tickLine={false} />
//           <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#a3a3a3' }} axisLine={false} tickLine={false} />
//           <Tooltip formatter={(v: number) => [`${v.toFixed(1)}%`, 'Usage']} />
//           <Bar dataKey="percent" radius={[4, 4, 0, 0]} barSize={18}>
//             {perCore.map((v, i) => (
//               <Cell key={i} fill={v > 90 ? '#dc2626' : v > 70 ? '#f59e0b' : '#2563eb'} />
//             ))}
//           </Bar>
//         </BarChart>
//       </ResponsiveContainer>
//     </ChartCard>
//   )
// }

// function Row({ label, value }: { label: string; value: string }) {
//   return (
//     <div className="flex items-center justify-between py-1.5 border-b border-neutral-50 last:border-0">
//       <span className="text-neutral-500">{label}</span>
//       <span className="font-semibold text-neutral-800 tabular-nums">{value}</span>
//     </div>
//   )
// }
